import { useContext } from 'react';
import NextSelection from '~/constants/new-cars/NextSelection';
import SectionName from '~/constants/new-cars/SectionName';
import ParentModelContext from '~/context/new-cars/ParentModelContext';
import type { CustomizeItem } from '~/contracts/new-cars';
import useReportMissingImages from './useReportMissingImages';

type TitledResult = { imageUrl: string; title: string };

function toVehicle(parts: Array<string | number | undefined>) {
  return parts.filter(Boolean).join(' ');
}

function fromResults(results: TitledResult[]) {
  return results.map(({ imageUrl, title }) => ({ imageUrl, name: title }));
}

function fromCustomizeItems(items: CustomizeItem[]) {
  return items.map(({ imageUrl, name }) => ({ imageUrl, name }));
}

export default function useReportConfiguratorMissingImages() {
  const {
    customizeOptions,
    isSectionIncluded,
    modelVersions,
    selectedDoors,
    selectedModelVersion,
    selectedParentModel,
    selectedTrim,
    selectedTruckBodyStyle,
    selectedYear,
    trims,
    truckBodyStyles,
    years
  } = useContext(ParentModelContext);

  const yearPage = [selectedParentModel.make, selectedParentModel.parentModel];
  const modelVersionPage = [...yearPage, selectedYear?.year];
  const trimPage = [
    ...modelVersionPage,
    (selectedDoors ?? selectedModelVersion)?.modelVersion
  ];
  const truckBodyStylePage = [...trimPage, selectedTrim?.trim];
  const optionsPage = [
    ...truckBodyStylePage,
    selectedTruckBodyStyle?.truckBodyStyle
  ];

  const modelVersionSection =
    modelVersions.data.nextSelection === NextSelection.DoorCount
      ? SectionName.Doors
      : SectionName.ModelVersion;

  useReportMissingImages({
    groups: [{ items: fromResults(years.results), section: SectionName.Year }],
    isLoaded: isSectionIncluded(SectionName.Year),
    vehicle: toVehicle(yearPage)
  });

  useReportMissingImages({
    groups: [
      {
        items: fromResults(modelVersions.data.results),
        section: modelVersionSection
      }
    ],
    isLoaded: modelVersions.isLoaded && isSectionIncluded(modelVersionSection),
    vehicle: toVehicle(modelVersionPage)
  });

  useReportMissingImages({
    groups: [
      { items: fromResults(trims.data.results), section: SectionName.Trim }
    ],
    isLoaded: trims.isLoaded && isSectionIncluded(SectionName.Trim),
    vehicle: toVehicle(trimPage)
  });

  useReportMissingImages({
    groups: [
      {
        items: fromResults(truckBodyStyles.data.results),
        section: SectionName.TruckBodyStyle
      }
    ],
    isLoaded:
      truckBodyStyles.isLoaded && isSectionIncluded(SectionName.TruckBodyStyle),
    vehicle: toVehicle(truckBodyStylePage)
  });

  useReportMissingImages({
    groups: [
      {
        items: fromCustomizeItems(customizeOptions.data.driveTypes),
        section: 'driveTypes'
      },
      {
        items: fromCustomizeItems(customizeOptions.data.engines),
        section: 'engines'
      },
      {
        items: fromCustomizeItems(customizeOptions.data.exteriorColors),
        section: 'exteriorColors'
      },
      {
        items: fromCustomizeItems(customizeOptions.data.interiorColors),
        section: 'interiorColors'
      },
      {
        items: fromCustomizeItems(
          customizeOptions.data.packageOptions.filter(
            ({ code }) => code !== 'NONE'
          )
        ),
        section: 'packageOptions'
      }
    ],
    isLoaded: customizeOptions.isLoaded,
    vehicle: toVehicle(optionsPage)
  });
}
